/**
 * Lining up a screenshot with the map (FD §3.5), shared by the editor's overlay panel and the wizard's
 * screenshot start:
 *   1. Measure: click two points on the screenshot and type the metres between them (sets the scale);
 *   2. Line up: a square shows the whole map on the screenshot; drag or nudge it, and turn the screenshot;
 *   3. Boundary (only when asked): click around the zoo's edge, or skip to keep a generated shape.
 * The result places the image on the map (TD §4 Overlay: top-left corner in map metres, metres per image pixel,
 * degrees clockwise) and gives the traced boundary in map metres.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from 'react';
import type { Pt } from '../../../../shared/types';
import '../editor.css';

export { downscaleImage } from './downscale';

/** What the calibrator hands back: where the image sits on the map, and the traced boundary (or null). */
export interface CalibrationResult {
  overlay: { x: number; y: number; scale: number; rotation: number; opacity: number };
  boundary: Pt[] | null;
}

export interface OverlayCalibratorProps {
  /** The (already downscaled) screenshot, e.g. an object URL or the plan's overlay file URL. */
  imageUrl: string;
  /** Map extent in metres (the map is a square). */
  mapSize: number;
  /** Also ask for the zoo boundary. */
  askBoundary: boolean;
  /** Start from an existing placement (re-calibrating): the square and turn are taken from it. */
  initial?: { x: number; y: number; scale: number; rotation: number; opacity: number };
  onDone(result: CalibrationResult): void;
  onCancel(): void;
}

type Step = 'measure' | 'align' | 'boundary';

const rad = (deg: number) => (deg * Math.PI) / 180;
/** Rotates v by `deg` degrees clockwise (screen axes: y down). */
const rot = (v: Pt, deg: number): Pt => {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg));
  return [v[0] * c - v[1] * s, v[0] * s + v[1] * c];
};

/** Image pixel of a map point, for scale s (m/px), turn r (deg) and p0 (image pixel of the map's top-left). */
function mapToImg(m: Pt, s: number, r: number, p0: Pt): Pt {
  const q = rot([m[0] / s, m[1] / s], -r);
  return [p0[0] + q[0], p0[1] + q[1]];
}
/** Map point of an image pixel (inverse of mapToImg). */
function imgToMap(u: Pt, s: number, r: number, p0: Pt): Pt {
  const q = rot([u[0] - p0[0], u[1] - p0[1]], r);
  return [q[0] * s, q[1] * s];
}
/** p0 that puts the map's centre on image pixel c. */
function p0ForCentre(c: Pt, s: number, r: number, mapSize: number): Pt {
  const h = rot([mapSize / 2 / s, mapSize / 2 / s], -r);
  return [c[0] - h[0], c[1] - h[1]];
}

/** The calibrator; see the file comment. */
export function OverlayCalibrator(props: OverlayCalibratorProps): JSX.Element {
  const { imageUrl, mapSize, askBoundary, initial, onDone, onCancel } = props;
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState<Step>(initial ? 'align' : 'measure');
  const [pts, setPts] = useState<Pt[]>([]);
  const [metres, setMetres] = useState('');
  const [scale, setScale] = useState<number>(initial?.scale ?? 0);
  const [turn, setTurn] = useState<number>(initial?.rotation ?? 0);
  const [p0, setP0] = useState<Pt | null>(null);
  const [opacity, setOpacity] = useState<number>(initial?.opacity ?? 0.6);
  const [ring, setRing] = useState<Pt[]>([]);
  const [view, setView] = useState({ zoom: 1, ox: 0, oy: 0 });
  const [box, setBox] = useState({ w: 600, h: 420 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drag = useRef<null | { mode: 'pan' | 'square' | 'point'; sx: number; sy: number; ox: number; oy: number; p0?: Pt; idx?: number }>(null);

  useEffect(() => {
    const im = new Image();
    im.onload = () => setImg(im);
    im.onerror = () => setLoadError("The screenshot couldn't be opened.");
    im.src = imageUrl;
  }, [imageUrl]);

  // Existing placement: recover p0 from the overlay's top-left corner (map position of image pixel 0,0).
  useEffect(() => {
    if (!initial || !img || p0) return;
    const q = rot([-initial.x / initial.scale, -initial.y / initial.scale], -initial.rotation);
    setP0(q);
  }, [initial, img, p0]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  /** Screen pixels per image pixel at zoom 1 (the picture fits the box). */
  const fit = img ? Math.min(box.w / img.naturalWidth, box.h / img.naturalHeight) : 1;
  const k = fit * view.zoom;
  const baseX = img ? (box.w - img.naturalWidth * fit) / 2 : 0, baseY = img ? (box.h - img.naturalHeight * fit) / 2 : 0;
  const toScreen = useCallback((u: Pt): Pt => [baseX + view.ox + u[0] * k, baseY + view.oy + u[1] * k], [baseX, baseY, view, k]);
  const toImg = useCallback((x: number, y: number): Pt => [(x - baseX - view.ox) / k, (y - baseY - view.oy) / k], [baseX, baseY, view, k]);

  const typed = Number(metres.replace(',', '.'));
  const pxDist = pts.length === 2 ? Math.hypot(pts[1]![0] - pts[0]![0], pts[1]![1] - pts[0]![1]) : 0;
  const canMeasure = pxDist > 2 && typed > 0 && Number.isFinite(typed);

  const squareCorners = useMemo((): Pt[] | null => {
    if (!p0 || !(scale > 0)) return null;
    return ([[0, 0], [mapSize, 0], [mapSize, mapSize], [0, mapSize]] as Pt[]).map((m) => mapToImg(m, scale, turn, p0));
  }, [p0, scale, turn, mapSize]);

  // draw
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(box.w * dpr);
    cv.height = Math.round(box.h * dpr);
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, box.w, box.h);
    if (!img) return;
    const [ix, iy] = toScreen([0, 0]);
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, ix, iy, img.naturalWidth * k, img.naturalHeight * k);
    const dot = (p: Pt, colour: string, r = 6) => {
      const [x, y] = toScreen(p);
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fillStyle = colour; g.fill();
      g.lineWidth = 2; g.strokeStyle = '#000'; g.stroke();
    };
    const line = (ps: Pt[], close: boolean, colour: string, width: number, dash: number[] = []) => {
      if (ps.length < 2) return;
      g.beginPath();
      ps.forEach((p, i) => { const [x, y] = toScreen(p); if (i) g.lineTo(x, y); else g.moveTo(x, y); });
      if (close) g.closePath();
      g.setLineDash(dash);
      g.lineWidth = width + 3; g.strokeStyle = 'rgba(0,0,0,0.6)'; g.stroke();
      g.lineWidth = width; g.strokeStyle = colour; g.stroke();
      g.setLineDash([]);
    };
    if (step === 'measure') {
      line(pts, false, '#ffeb3b', 2.5, [8, 5]);
      pts.forEach((p, i) => dot(p, i ? '#ff9800' : '#ffeb3b'));
    }
    if ((step === 'align' || step === 'boundary') && squareCorners) {
      line(squareCorners, true, '#3ddc84', step === 'align' ? 3 : 2, step === 'align' ? [] : [6, 6]);
      const [x, y] = toScreen(squareCorners[0]!);
      g.font = 'bold 13px system-ui, sans-serif';
      g.fillStyle = '#3ddc84'; g.strokeStyle = 'rgba(0,0,0,0.75)'; g.lineWidth = 3;
      g.strokeText('Your map', x + 6, y + 18); g.fillText('Your map', x + 6, y + 18);
    }
    if (step === 'boundary') {
      line(ring, ring.length > 2, '#ffeb3b', 3);
      ring.forEach((p) => dot(p, '#ffeb3b', 4.5));
    }
  }, [img, box, k, toScreen, step, pts, squareCorners, ring]);

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const nz = Math.min(12, Math.max(1, view.zoom * Math.pow(1.0018, -e.deltaY)));
    const f = nz / view.zoom;
    // keep the image pixel under the cursor in place
    setView({ zoom: nz, ox: x - baseX - (x - baseX - view.ox) * f, oy: y - baseY - (y - baseY - view.oy) * f });
  };
  // A native listener, so the wheel zooms the picture instead of scrolling the page.
  const wheelRef = useRef(onWheel);
  wheelRef.current = onWheel;
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const h = (e: WheelEvent) => wheelRef.current(e);
    cv.addEventListener('wheel', h, { passive: false });
    return () => cv.removeEventListener('wheel', h);
  }, []);

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!img) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top, u = toImg(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
    const pan = () => { drag.current = { mode: 'pan', sx: x, sy: y, ox: view.ox, oy: view.oy }; };
    if (e.button !== 0) return pan();
    const near = (p: Pt) => Math.hypot((p[0] - u[0]) * k, (p[1] - u[1]) * k) < 10;
    if (step === 'measure') {
      const idx = pts.findIndex(near);
      if (idx >= 0) { drag.current = { mode: 'point', sx: x, sy: y, ox: 0, oy: 0, idx }; return; }
      if (pts.length < 2) { setPts([...pts, u]); return; }
      return pan();
    }
    if (step === 'align') {
      if (squareCorners && p0 && insideQuad(squareCorners, u)) {
        drag.current = { mode: 'square', sx: x, sy: y, ox: 0, oy: 0, p0 };
        return;
      }
      return pan();
    }
    if (step === 'boundary') {
      if (e.detail >= 2) return; // the double-click finishes (below)
      setRing([...ring, u]);
    }
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    if (d.mode === 'pan') setView({ ...view, ox: d.ox + x - d.sx, oy: d.oy + y - d.sy });
    else if (d.mode === 'square' && d.p0) setP0([d.p0[0] + (x - d.sx) / k, d.p0[1] + (y - d.sy) / k]);
    else if (d.mode === 'point' && d.idx !== undefined) { const u = toImg(x, y); setPts(pts.map((p, i) => (i === d.idx ? u : p))); }
  };
  const onPointerUp = () => { drag.current = null; };

  const goAlign = () => {
    if (!img || !canMeasure) return;
    const s = typed / pxDist;
    setScale(s);
    setP0(p0 && scale > 0 ? p0ForCentre(centreOf(p0, scale, turn, mapSize), s, turn, mapSize) : p0ForCentre([img.naturalWidth / 2, img.naturalHeight / 2], s, turn, mapSize));
    setStep('align');
  };
  const setTurnKeepCentre = (deg: number) => {
    const t = Math.max(-180, Math.min(180, deg));
    if (p0 && scale > 0) setP0(p0ForCentre(centreOf(p0, scale, turn, mapSize), scale, t, mapSize));
    setTurn(t);
  };
  const nudge = (dx: number, dy: number) => {
    if (!p0 || !(scale > 0)) return;
    // one press moves the map 1 m over the screenshot
    const q = rot([dx / scale, dy / scale], -turn);
    setP0([p0[0] - q[0], p0[1] - q[1]]);
  };

  const finish = (boundary: Pt[] | null) => {
    if (!p0 || !(scale > 0)) return;
    const tl = imgToMap([0, 0], scale, turn, p0);
    onDone({
      overlay: { x: tl[0], y: tl[1], scale, rotation: turn, opacity },
      boundary: boundary ? boundary.map((u) => {
        const m = imgToMap(u, scale, turn, p0);
        return [Math.min(mapSize, Math.max(0, m[0])), Math.min(mapSize, Math.max(0, m[1]))] as Pt;
      }) : null,
    });
  };

  const outsideShare = useMemo(() => {
    if (!p0 || !(scale > 0) || ring.length < 3) return 0;
    const out = ring.filter((u) => { const m = imgToMap(u, scale, turn, p0); return m[0] < 0 || m[1] < 0 || m[0] > mapSize || m[1] > mapSize; });
    return out.length / ring.length;
  }, [ring, p0, scale, turn, mapSize]);

  const stepNo = step === 'measure' ? 1 : step === 'align' ? 2 : 3;
  const steps = askBoundary ? 3 : 2;

  return (
    <div className="calibrator">
      <div className="calibrator-head">
        <strong>Step {stepNo} of {steps}: {step === 'measure' ? 'Measure' : step === 'align' ? 'Line it up' : 'Trace the zoo boundary'}</strong>
        <span className="muted small">Scroll to zoom in, drag with the right mouse button to move around.</span>
      </div>
      <div className="calibrator-stage" ref={wrapRef}>
        {loadError ? (
          <p className="error calibrator-msg">{loadError}</p>
        ) : !img ? (
          <p className="muted calibrator-msg">Opening the screenshot…</p>
        ) : null}
        <canvas
          ref={canvasRef}
          style={{ width: box.w, height: box.h, cursor: step === 'align' ? 'move' : 'crosshair' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => { if (step === 'boundary' && ring.length >= 3) finish(ring); }}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
      <div className="calibrator-panel">
        {step === 'measure' && (
          <>
            <p>
              Click two points on the screenshot that you know the distance between, then type that distance.
              {pts.length === 0 && ' Click the first point.'}
              {pts.length === 1 && ' Now click the second point.'}
              {pts.length === 2 && ' You can drag the points to fine-tune them.'}
            </p>
            <p className="hint">
              How to measure: Planet Zoo 2 has no ruler, so use something you know the size of. For example, build a straight
              path and note its length while you place it, or count grid squares, and take the screenshot with it in view.
              The farther apart the two points are, the more exact the sizes will be.
            </p>
            <div className="row wrap">
              <label className="field inline">
                <span>Distance between the points</span>
                <span className="row">
                  <input type="number" min={0} step="any" inputMode="decimal" value={metres} placeholder="e.g. 40" onChange={(e) => setMetres(e.target.value)} style={{ width: '8em' }} />
                  metres
                </span>
              </label>
              {pts.length > 0 && <button type="button" onClick={() => setPts([])}>Start the points again</button>}
            </div>
          </>
        )}
        {step === 'align' && (
          <>
            <p>
              The green square is your whole map ({Math.round(mapSize)} m across). Drag it, or use the arrows, so it covers the part of
              the screenshot you want to plan. Turn the screenshot if the map should sit straight.
            </p>
            <div className="row wrap">
              <span className="nudge" aria-label="Nudge the map">
                <button type="button" title="Move the map 1 m left" onClick={() => nudge(1, 0)}>←</button>
                <button type="button" title="Move the map 1 m up" onClick={() => nudge(0, 1)}>↑</button>
                <button type="button" title="Move the map 1 m down" onClick={() => nudge(0, -1)}>↓</button>
                <button type="button" title="Move the map 1 m right" onClick={() => nudge(-1, 0)}>→</button>
              </span>
              <label className="field inline grow">
                <span>Turn the screenshot: {turn.toFixed(1).replace(/\.0$/, '')}°</span>
                <span className="row">
                  <button type="button" onClick={() => setTurnKeepCentre(turn - 0.5)} title="Turn a little to the left">⟲</button>
                  <input type="range" min={-180} max={180} step={0.5} value={turn} onChange={(e) => setTurnKeepCentre(Number(e.target.value))} />
                  <button type="button" onClick={() => setTurnKeepCentre(turn + 0.5)} title="Turn a little to the right">⟳</button>
                </span>
              </label>
              <label className="field inline">
                <span>See-through under the map</span>
                <input type="range" min={0.1} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} />
              </label>
            </div>
            <p className="muted small">1 pixel of the screenshot is {scale > 0 ? (scale < 1 ? `${(scale * 100).toFixed(1)} cm` : `${scale.toFixed(2)} m`) : '?'}.</p>
          </>
        )}
        {step === 'boundary' && (
          <>
            <p>Click around the edge of your zoo. Double-click or press Finish to close the shape.</p>
            {outsideShare > 0 && <p className="note">Some points are outside the green map square; they'll be pulled onto its edge.</p>}
            <div className="row wrap">
              <button type="button" disabled={!ring.length} onClick={() => setRing(ring.slice(0, -1))}>Undo last point</button>
              <button type="button" disabled={!ring.length} onClick={() => setRing([])}>Start again</button>
            </div>
          </>
        )}
        <div className="dialog-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          {step !== 'measure' && (
            <button type="button" onClick={() => setStep(step === 'boundary' ? 'align' : 'measure')}>Back</button>
          )}
          {step === 'measure' && (
            <button type="button" className="primary" disabled={!canMeasure} onClick={goAlign}>Next</button>
          )}
          {step === 'align' && !askBoundary && (
            <button type="button" className="primary" disabled={!p0} onClick={() => finish(null)}>Done</button>
          )}
          {step === 'align' && askBoundary && (
            <button type="button" className="primary" disabled={!p0} onClick={() => setStep('boundary')}>Next</button>
          )}
          {step === 'boundary' && (
            <>
              <button type="button" onClick={() => finish(null)}>Skip, make a shape for me</button>
              <button type="button" className="primary" disabled={ring.length < 3} onClick={() => finish(ring)}>Finish</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Image pixel of the map's centre. */
function centreOf(p0: Pt, s: number, r: number, mapSize: number): Pt {
  return mapToImg([mapSize / 2, mapSize / 2], s, r, p0);
}

/** True when u lies inside the (convex) quad. */
function insideQuad(q: Pt[], u: Pt): boolean {
  let sign = 0;
  for (let i = 0; i < q.length; i++) {
    const a = q[i]!, b = q[(i + 1) % q.length]!;
    const c = (b[0] - a[0]) * (u[1] - a[1]) - (b[1] - a[1]) * (u[0] - a[0]);
    if (c !== 0) {
      if (sign && Math.sign(c) !== sign) return false;
      sign = Math.sign(c);
    }
  }
  return true;
}
